/**
 * Single source of truth for serializing Location Settings.
 *
 * Mirrors the architecture of `serializeCardSettings.js`. See that file's
 * doc-block for the rationale. Same INVARIANT applies:
 *
 *   Both the snapshot (built from the API location object) AND the live
 *   form state MUST be produced by THIS exact same function. Adding a new
 *   setting = ONE row in FIELDS — both sides automatically stay in sync.
 *
 * Note: location settings include a flat top-level (name, address, org_id)
 * plus a nested `baseline_values` object. The serializer flattens the
 * baseline values into the output JSON for stable comparison.
 */

// ── Coercion helpers (same buckets as serializeCardSettings) ──

const _toIntOrNull = (v) => {
    if (v === null || v === undefined || v === '') return null;
    const n = parseInt(v);
    return Number.isFinite(n) ? n : null;
};

const _toFloatOrNull = (v) => {
    if (v === null || v === undefined || v === '') return null;
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : null;
};

const _toStr = (v, d = '') => (v === null || v === undefined ? d : String(v).trim());

const _toBool = (v) => Boolean(v);
const _toBoolDefaultTrue = (v) => v !== false;
const _toArray = (v) => (Array.isArray(v) ? v : []);
const _toObject = (v) => (v && typeof v === 'object' && !Array.isArray(v) ? v : {});
const _orNull = (v) => (v === undefined ? null : v);

// ── Field registry — ONE place ──
//
// Each entry: { key, location, state, type, default, baseline? }
//   key:       output JSON key
//   location:  field name on the API location object
//   state:     field name on the React form state
//   type:      coercion bucket
//   default:   value when the source is missing/null/invalid
//   baseline:  if true, source is read from `obj.baseline_values[location]`
//              for the location form (state still uses formData.baseline_values[state])
const FIELDS = [
    // Top-level
    { key: 'name',            location: 'name',            state: 'name',            type: 'str',  default: '' },
    { key: 'address',         location: 'address',         state: 'address',         type: 'str',  default: '' },
    { key: 'organizationId',  location: 'organization_id', state: 'organization_id', type: 'str',  default: '' },

    // Baseline values (nested under `baseline_values` on location; flat in form state)
    { key: 'baselineDate',          baseline: true, location: 'baseline_date',          state: 'baseline_date',          type: 'str',   default: '' },
    { key: 'googleReviews',         baseline: true, location: 'google_reviews',         state: 'google_reviews',         type: 'intN',  default: null },
    { key: 'googleRating',          baseline: true, location: 'google_rating',          state: 'google_rating',          type: 'floatN', default: null },
    { key: 'facebookLikes',         baseline: true, location: 'facebook_likes',         state: 'facebook_likes',         type: 'intN',  default: null },
    { key: 'instagramFollowers',    baseline: true, location: 'instagram_followers',    state: 'instagram_followers',    type: 'intN',  default: null },
    { key: 'yelpReviews',           baseline: true, location: 'yelp_reviews',           state: 'yelp_reviews',           type: 'intN',  default: null },
    { key: 'yelpRating',            baseline: true, location: 'yelp_rating',            state: 'yelp_rating',            type: 'floatN', default: null },
    { key: 'trustpilotReviews',     baseline: true, location: 'trustpilot_reviews',     state: 'trustpilot_reviews',     type: 'intN',  default: null },
    { key: 'trustpilotRating',      baseline: true, location: 'trustpilot_rating',      state: 'trustpilot_rating',      type: 'floatN', default: null },
];

function normalize(v, type, def) {
    switch (type) {
        case 'int':      return _toIntOrNull(v) ?? def;
        case 'intN':     return _toIntOrNull(v);
        case 'float':    return _toFloatOrNull(v) ?? def;
        case 'floatN':   return _toFloatOrNull(v);
        case 'bool':     return _toBool(v);
        case 'boolDT':   return _toBoolDefaultTrue(v);
        case 'array':    return _toArray(v);
        case 'object':   return _toObject(v);
        case 'str':      return _toStr(v, def);
        case 'nullable': return _orNull(v);
        default:         return v ?? def;
    }
}

/**
 * Serialize either a location (from the API) or a flat form-state object
 * to a stable JSON string.
 *
 * @param {Object} input  Either { name, address, organization_id, baseline_values: {...} }
 *                        OR     { name, address, organization_id, baseline_values: {...} }
 *                        (form state has same top-level shape — see openModal in Locations.jsx)
 * @param {'location'|'state'} source  Tells the function which side it's reading.
 * @returns {string} JSON string suitable for === comparison.
 */
export function serializeLocationSettings(input, source = 'location') {
    if (!input || typeof input !== 'object') return JSON.stringify({});
    const baseline = (source === 'location'
        ? (input.baseline_values || {})
        : (input.baseline_values || {}));

    // P_PORTAL_SAVE_INTEGRITY_AUDIT — dev-time coverage guard.
    if (
        source === 'state'
        && typeof process !== 'undefined'
        && process.env
        && process.env.NODE_ENV !== 'production'
    ) {
        if (!serializeLocationSettings._warned) serializeLocationSettings._warned = new Set();
        for (const f of FIELDS) {
            const bag = f.baseline ? baseline : input;
            if (!Object.prototype.hasOwnProperty.call(bag, f.state)
                && !serializeLocationSettings._warned.has(f.state)) {
                serializeLocationSettings._warned.add(f.state);
                // eslint-disable-next-line no-console
                console.error(
                    `[SAVE_INTEGRITY] serializeLocationSettings(state) missing key: "${f.state}"` +
                    (f.baseline ? ' (under baseline_values)' : '') +
                    '. Save Changes will never enable for this field.'
                );
            }
        }
    }

    const out = {};
    for (const f of FIELDS) {
        let raw;
        if (f.baseline) {
            // For both 'location' and 'state', baseline values live in the same
            // nested `baseline_values` object. Different field names per source.
            const bag = baseline;
            raw = source === 'location' ? bag[f.location] : bag[f.state];
        } else {
            raw = source === 'location' ? input[f.location] : input[f.state];
        }
        out[f.key] = normalize(raw, f.type, f.default);
    }
    return JSON.stringify(out);
}

/** Convenience export — list of canonical keys. */
export const LOCATION_SETTING_KEYS = FIELDS.map(f => f.key);
